// Contrail V3.7.0 — the phone app at /m: bottom tabs Map · Objects · Alerts · More, safe-area padding,
// offline banner, "Update available" toast and a one-time install hint. The map stays mounted behind the other tabs
// so switching tabs never reloads it.
import { quietNow, useUserPrefs } from "../lib/prefs";
import { useCallback, useEffect, useRef, useState } from "react";
import { NavLink, useLocation, useNavigate } from "react-router-dom";
import type { Session } from "@supabase/supabase-js";
import type { Map as MLMap } from "maplibre-gl";
import { supabase } from "../lib/supabase";
import { useLiveDevices } from "../lib/useLiveDevices";
import { useStaffRole } from "../lib/admin";
import { useBasemaps } from "../ui/BasemapPicker";
import { useAlertEvents } from "../routes/map/EventsPanel";
import type { Zone } from "../lib/org";
import type { Status } from "../lib/format";
import { applyUpdate, isStandalone, updateReady } from "../lib/pwa";
import MobileMap, { type MyPos } from "./MobileMap";
import ObjectSheet from "./ObjectSheet";
import ObjectList from "./ObjectList";
import MobileAlerts from "./MobileAlerts";
import MoreTab from "./MoreTab";
import InstallPrompt, { useInstallable } from "./InstallPrompt";
import "./mobile.css";

type Tab = "map" | "objects" | "alerts" | "more";
const tabOf = (p: string): Tab => (p.startsWith("/m/objects") ? "objects" : p.startsWith("/m/alerts") ? "alerts" : p.startsWith("/m/more") ? "more" : "map");

export default function MobileShell({ session }: { session: Session }) {
  const loc = useLocation(); const nav = useNavigate();
  const tab = tabOf(loc.pathname);
  const { devices, loaded, error, now, feed } = useLiveDevices();
  const staff = useStaffRole(session.user.id);
  const basemaps = useBasemaps(staff === undefined ? undefined : !!staff);
  const [events, setEvents] = useAlertEvents();
  const prefs = useUserPrefs();
  const quiet = quietNow(prefs, now).quiet;
  const unread = quiet ? 0 : events.filter((e) => !e.ack_at).length;   // V3.9.0: no badge while snoozed
  const [zones, setZones] = useState<Zone[]>([]);
  useEffect(() => { supabase.from("geofences").select("*").order("name").then(({ data }) => setZones((data ?? []) as Zone[])); }, []);

  const [selected, setSelected] = useState<string | null>(null);
  const [followId, setFollowId] = useState<string | null>(null);
  const [filter, setFilter] = useState<Status | null>(null);
  const [me, setMe] = useState<MyPos | null>(null);
  const mapRef = useRef<MLMap | null>(null);
  const onMap = useCallback((m: MLMap | null) => { mapRef.current = m; }, []);

  // toasts / banners
  const [toast, setToast] = useState<string | null>(null);
  const toastT = useRef<number | undefined>(undefined);
  const showToast = useCallback((m: string) => { setToast(m); window.clearTimeout(toastT.current); toastT.current = window.setTimeout(() => setToast(null), 5000); }, []);
  const [online, setOnline] = useState(navigator.onLine);
  useEffect(() => {
    const on = () => setOnline(true), off = () => setOnline(false);
    window.addEventListener("online", on); window.addEventListener("offline", off);
    return () => { window.removeEventListener("online", on); window.removeEventListener("offline", off); };
  }, []);
  const [update, setUpdate] = useState(updateReady());
  useEffect(() => { const f = () => setUpdate(true); window.addEventListener("fw-update", f); return () => window.removeEventListener("fw-update", f); }, []);
  const installable = useInstallable();
  const [hintSeen, setHintSeen] = useState(() => { try { return localStorage.getItem("fw.installHint") === "1"; } catch { return true; } });
  const dismissHint = () => { setHintSeen(true); try { localStorage.setItem("fw.installHint", "1"); } catch { /* ignore */ } };

  const pick = (id: string) => {
    setSelected(id); setFollowId(null);
    const s = devices.find((d) => d.id === id)?.state;
    nav("/m");
    if (s?.lat != null && s.lng != null) setTimeout(() => mapRef.current?.flyTo({ center: [s.lng!, s.lat!], zoom: Math.max(mapRef.current.getZoom(), 15) }), 50);
  };
  const openAt = (id: string, lat: number | null, lng: number | null) => {
    setSelected(id); setFollowId(null); nav("/m");
    if (lat != null && lng != null) setTimeout(() => mapRef.current?.flyTo({ center: [lng, lat], zoom: Math.max(mapRef.current.getZoom(), 15) }), 50);
  };
  const sel = devices.find((d) => d.id === selected) ?? null;
  useEffect(() => { if (selected && loaded && !sel) setSelected(null); }, [selected, loaded, sel]);

  return (
    <div className="m-app">
      {!online && <div className="m-banner off" role="status">Offline: showing last known positions</div>}
      {online && feed === "reconnecting" && <div className="m-banner off" role="status">Reconnecting… positions will refresh in a moment</div>}
      {error && <div className="m-banner bad" role="alert">Couldn't load your objects: {error}</div>}
      <main className="m-stage">
        <MobileMap devices={devices.filter((d) => !prefs.hidden_devices.includes(d.id))} now={now} zones={zones} basemaps={basemaps} selectedId={selected} followId={followId} filter={filter}
          onSelect={(id) => { setSelected(id); if (!id) setFollowId(null); }} onFollowEnd={() => setFollowId(null)} onMyPos={setMe}
          onMap={onMap} onToast={showToast} onFilter={setFilter} />
        {tab === "map" && sel && (
          <ObjectSheet device={sel} zones={zones} now={now} me={me} following={followId === sel.id}
            onFollow={(on) => setFollowId(on ? sel.id : null)} onClose={() => { setSelected(null); setFollowId(null); }} onToast={showToast} />
        )}
        {tab === "map" && !sel && !hintSeen && !isStandalone() && (installable || /iphone|ipad/i.test(navigator.userAgent)) && (
          <div className="m-hint">
            <b>Get the app</b>
            <InstallPrompt compact onDone={dismissHint} />
            <button type="button" className="m-x" onClick={dismissHint} aria-label="Dismiss">×</button>
          </div>
        )}
        {tab === "objects" && <ObjectList devices={devices} now={now} filter={filter} onFilter={setFilter} onPick={pick} />}
        {tab === "alerts" && <MobileAlerts events={events} setEvents={setEvents} devices={devices} now={now} onOpen={openAt} />}
        {tab === "more" && <MoreTab session={session} />}
      </main>
      {update && <button type="button" className="m-toast m-update" onClick={applyUpdate}>Update available — tap to refresh</button>}
      {toast && <div className="m-toast" role="status" onClick={() => setToast(null)}>{toast}</div>}
      <nav className="m-tabs" aria-label="Sections">
        <NavLink to="/m" end className={() => (tab === "map" ? "active" : "")}>
          <svg viewBox="0 0 24 24" width="22" height="22" fill="none" stroke="currentColor" strokeWidth="2" aria-hidden="true"><path d="M9 4 3 6v14l6-2 6 2 6-2V4l-6 2-6-2Z" /><path d="M9 4v14M15 6v14" /></svg><span>Map</span></NavLink>
        <NavLink to="/m/objects" className={() => (tab === "objects" ? "active" : "")}>
          <svg viewBox="0 0 24 24" width="22" height="22" fill="none" stroke="currentColor" strokeWidth="2" aria-hidden="true"><path d="M8 6h13M8 12h13M8 18h13" /><circle cx="4" cy="6" r="1" /><circle cx="4" cy="12" r="1" /><circle cx="4" cy="18" r="1" /></svg><span>Objects</span></NavLink>
        <NavLink to="/m/alerts" className={() => (tab === "alerts" ? "active" : "")}>
          <span className="m-ico">
            <svg viewBox="0 0 24 24" width="22" height="22" fill="none" stroke="currentColor" strokeWidth="2" aria-hidden="true"><path d="M6 8a6 6 0 1 1 12 0c0 7 3 9 3 9H3s3-2 3-9" /><path d="M10.3 21a1.9 1.9 0 0 0 3.4 0" /></svg>
            {unread > 0 && <i className="m-badge" aria-label={`${unread} new`}>{unread > 99 ? "99+" : unread}</i>}
          </span><span>Alerts</span></NavLink>
        <NavLink to="/m/more" className={() => (tab === "more" ? "active" : "")}>
          <svg viewBox="0 0 24 24" width="22" height="22" fill="none" stroke="currentColor" strokeWidth="2" aria-hidden="true"><circle cx="5" cy="12" r="1.5" /><circle cx="12" cy="12" r="1.5" /><circle cx="19" cy="12" r="1.5" /></svg><span>More</span></NavLink>
      </nav>
    </div>
  );
}
